import type { WhiteListItem } from 'src/components/root/declare'
import type { EvtConfig, EditControlConfig } from '../declare'
import { useState, useEffect, useContext } from 'react'
import { autorun, reaction } from 'mobx'
import { Form, Tooltip, Row, Col, Input, InputNumber } from 'antd'
import { QuestionCircleOutlined, RightOutlined } from '@ant-design/icons'
import * as Pager from './pager'
import { EditPropsContext, EditContext } from 'src/components'
import { $consts, $evt } from 'src/plugins'
import { get, isEqual } from 'lodash'

const useEvtConfig = (FIELD_NAME: string) => {
  const edit = useContext(EditContext)

  // 更新编辑表单白名单
  const updateWhiteList = (data: WhiteListItem, white: boolean) => {
    // 白名单设置
    const { verifyWhiteList, setVerifyWhiteList } = edit
    if (white) {
      // 加入白名单
      setVerifyWhiteList(([] as WhiteListItem[]).concat(verifyWhiteList, data))
    } else {
      // 恢复白名单
      setVerifyWhiteList(verifyWhiteList.filter((item: WhiteListItem) => item.field !== data.field))
    }
  }

  const [evtConfig, setEvtConfig] = useState<EvtConfig>({})
  useEffect(() => {
    const controlEditable = (fields: string[], editable: boolean) => {
      fields.forEach((field: string) => {
        if (field === FIELD_NAME) {
          const disabled = !editable
          setEvtConfig(prev => {
            const curr = { ...prev, disabled }
            return isEqual(prev, curr) ? prev : curr
          })
          updateWhiteList({ field, disabled }, disabled)
        }
      })
    }
    const controlVisible = (fields: string[], isVisible: boolean) => {
      fields.forEach((field: string) => {
        if (field === FIELD_NAME) {
          const hidden = !isVisible
          setEvtConfig(prev => {
            const curr = { ...prev, hidden }
            return isEqual(prev, curr) ? prev : curr
          })
          updateWhiteList({ field, hidden }, hidden)
        }
      })
    }

    $evt.addListener($consts['EVT/CONTROL_EDITABLE'], controlEditable)
    $evt.addListener($consts['EVT/CONTROL_VISIBLE'], controlVisible)

    return () => {
      $evt.removeListener($consts['EVT/CONTROL_EDITABLE'], controlEditable)
      $evt.removeListener($consts['EVT/CONTROL_VISIBLE'], controlVisible)
    }
  }, [FIELD_NAME])

  return evtConfig
}

/**
 * 编辑表单表单配置控件
 * @param props 
 * @returns 
 */
const PagerControl = (props: { config: EditControlConfig }) => {
  const editProps = useContext(EditPropsContext)
  const edit = useContext(EditContext)

  // 控件值
  const [value, setValue] = useState()
  useEffect(() => autorun(() => {
    const val = get(edit?.formBase, props.config.FIELD_NAME)
    setValue(prev => isEqual(prev, val) ? prev : val)
  }), [props.config.FIELD_NAME])

  // 控件 change 事件
  const handleChange = (value: any, extra: any) => {
    const { FIELD_NAME, CHANGE_FUNC } = props.config
    if (edit) {
      // 表单数据
      edit.setFormBase({ [FIELD_NAME]: value, ...extra })
      // 是否配置了动态加载 js 中的方法名
      const dynamicPayload = { value, extra }
      edit.dynamicJs[CHANGE_FUNC]?.(edit, FIELD_NAME, dynamicPayload)
      // 调用动态加载 js 中全局 onChange 事件
      edit.dynamicJs[$consts['LIFECYCLE/EDIT_PAGE_FORM_CHANGE']]?.(edit, FIELD_NAME, dynamicPayload)
    }
  }

  // 批量修改控件值
  const [batch, setBatch] = useState()
  useEffect(() => autorun(() => {
    const val = get(edit?.formBatch, props.config.FIELD_NAME)
    setBatch(val ?? $consts['EDIT/BATCH_TEXTINPUT_UPDATE'])
  }), [props.config.FIELD_NAME])

  // 批量替换的值保存
  const handleBatchReplace = (value: any) => {
    const { FIELD_NAME } = props.config
    edit?.setFormBatch({ [`${FIELD_NAME}__REPLACE__`]: value })
  }

  // 批量修改控件 change 事件
  const handleBatchChange = (value: any) => {
    const { FIELD_NAME } = props.config
    edit?.setFormBatch({ [FIELD_NAME]: value })
  }

  // 监听控件相关事件
  const evtConfig = useEvtConfig(props.config.FIELD_NAME)

  // 控件加载完毕事件
  useEffect(() => {
    edit.dynamicJs[$consts['LIFECYCLE/EDIT_PAGE_CONTROL_LOADED']]?.(edit, { current: editProps }, props.config)
    $evt.emit($consts['LIFECYCLE/EDIT_PAGE_CONTROL_LOADED'], {
      state: edit,
      latestProps: editProps,
      config: props.config
    })
  }, [])

  // 控件校验
  const [verify, setVerify] = useState({ invalid: false, msg: undefined })
  useEffect(() => reaction(
    () => get(edit?.verify, props.config.FIELD_NAME),
    data => setVerify(prev => isEqual(prev, data) ? prev : data)
  ), [props.config.FIELD_NAME])

  // 获取 Form.Item label
  const getLabel = () => {
    const { LABEL, VERIFY_ERROR_INFO, REMARK } = props.config
    const question = VERIFY_ERROR_INFO || REMARK
    if (question) return <span><span className='pd-r4'>{LABEL}</span><Tooltip title={question}><QuestionCircleOutlined /></Tooltip></span>
    return <span>{LABEL}</span>
  }

  // 根据 CONTROL_TYPE 获取对应控件
  const getControl = () => {
    const { ON_RENDER, CONTROL_TYPE } = props.config
    // 配置了自定义渲染函数
    if (edit.dynamicJs[ON_RENDER]) {
      return edit.dynamicJs[ON_RENDER]({
        value,
        config: props.config,
        evtConfig,
        onChange: handleChange
      })
    }

    // 渲染对应控件类型
    switch (CONTROL_TYPE) {
      // 文本输入框
      case $consts['TABLE/CONTROL_TYPE_TEXTINPUT']:
        return (
          <Pager.TextInput
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 时间控件类型
      case $consts['TABLE/CONTROL_TYPE_DATE_DAY_SECOND']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_SECOND']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_MINUTE']:
      case $consts['TABLE/CONTROL_TYPE_DATE_DAY']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY']:
      case $consts['TABLE/CONTROL_TYPE_DATE_MONTH']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_MONTH']:
      case $consts['TABLE/CONTROL_TYPE_DATE_YEAR']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_YEAR']:
        return (
          <Pager.DateControl
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 枚举下拉单选框
      case $consts['TABLE/CONTROL_TYPE_ENUM_SELECT']:
        return (
          <Pager.EnumSelect
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 单选 Switch 控件
      case $consts['TABLE/CONTROL_TYPE_SWITCH']:
        return (
          <Pager.SwitchControl
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 复选框控件
      case $consts['TABLE/CONTROL_TYPE_CHECKBOX']:
        return (
          <Pager.CheckboxControl
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 长文本输入框
      case $consts['TABLE/CONTROL_TYPE_TEXTAREA']:
        return (
          <Pager.AreaInput
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 数字输入框、小数精度
      case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT']:
      case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_TWODOT']:
      case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_THREEDOT']:
        return (
          <Pager.NumberInput
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 分页下拉
      case $consts['TABLE/CONTROL_TYPE_PAGINATION_SELECT']:
        return (
          <Pager.PaginationSelect
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 图片/文件选择
      case $consts['TABLE/CONTROL_TYPE_FILE_SELECT']:
        return (
          <Pager.FileSelect
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 树下拉
      case $consts['TABLE/CONTROL_TYPE_TREE_SELECT']:
        return (
          <Pager.TreeSelect
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 级联选择
      case $consts['TABLE/CONTROL_TYPE_CASCADER_SELECT']:
        return (
          <Pager.CascaderSelect
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 系统序列号控件
      case $consts['TABLE/CONTROL_TYPE_SERIES_NO']:
        return (
          <Pager.SeriesNo
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 密码控件
      case $consts['TABLE/CONTROL_TYPE_PASSWORD']:
        return (
          <Pager.PasswordInput
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 分页下拉多选
      case $consts['TABLE/CONTROL_TYPE_PAGINATION_MULTI_SELECT']:
        return (
          <Pager.PaginationMultiSelect
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 附件上传
      case $consts['TABLE/CONTROL_TYPE_ATTACH_UPLOAD']:
        if ($consts['CONFIG/APP_CODE'] === 'SY') {
          return (
            <Pager.AttachUploadEMS
              value={value}
              config={props.config}
              evtConfig={evtConfig}
              onChange={handleChange}
            />
          )
        }
        // enas旧版附件上传
        if (
          $consts['CONFIG/APP_CODE'] === 'CFG'
          || $consts['CONFIG/APP_CODE'] === 'RTM'
          || Array.isArray(value)
        ) {
          return (
            <Pager.AttachUploadV1
              value={value}
              config={props.config}
              evtConfig={evtConfig}
              onChange={handleChange}
            />
          )
        }
        return (
          <Pager.AttachUpload
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      // 文件上传
      case $consts['TABLE/CONTROL_TYPE_FILE_UPLOAD']:
        return (
          <Pager.FileUpload
            value={value}
            config={props.config}
            evtConfig={evtConfig}
            onChange={handleChange}
          />
        )
      default:
        return <div />
    }
  }

  const { hidden = false } = evtConfig
  if (hidden) return null

  // 批量编辑
  if (editProps.formData?.batchEdit) {
    const batchReplace = batch === $consts['EDIT/BATCH_TEXTINPUT_REPLACE']
    const batchDate = (
      batch === $consts['EDIT/BATCH_DATE_DAY_PLUS_DAY']
      || batch === $consts['EDIT/BATCH_DATE_DAY_PLUS_MONTH']
      || batch === $consts['EDIT/BATCH_DATE_DAY_PLUS_YEAR']
      || batch === $consts['EDIT/BATCH_DATE_DAY_SUB_DAY']
      || batch === $consts['EDIT/BATCH_DATE_DAY_SUB_MONTH']
      || batch === $consts['EDIT/BATCH_DATE_DAY_SUB_YEAR']
    )
    return (
      <Form.Item label={getLabel()}>
        <Row gutter={8}>
          <Col span={batchReplace ? 8 : 10}>
            <Pager.BatchEditControl
              value={batch}
              config={props.config}
              evtConfig={evtConfig}
              onChange={handleBatchChange}
            />
          </Col>
          {
            batchReplace
              ? (
                <Col span={9} className='display-flex'>
                  <Input onChange={evt => handleBatchReplace(evt.target.value)} />
                  <RightOutlined className='mg-l8' />
                </Col>
              )
              : null
          }
          <Col span={batchReplace ? 7 : 14}>
            {batchDate ? <InputNumber onChange={val => handleBatchReplace(val)} /> : getControl()}
          </Col>
        </Row>
      </Form.Item>
    )
  }

  const { REQUIRED } = props.config
  return (
    <Form.Item
      required={REQUIRED}
      label={getLabel()}
      validateStatus={verify.invalid ? 'error' : undefined}
      help={verify.invalid ? verify.msg : undefined}
    >
      {getControl()}
    </Form.Item>
  )
}

export default PagerControl